import { CreditCard, LogOut, Menu, X } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface NavbarProps {
  mobileMenuOpen?: boolean;
  onToggleMobileMenu?: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({ mobileMenuOpen, onToggleMobileMenu }) => {
  const { user, subscription, logout } = useAuth();

  return (
    <header className="h-16 border-b border-slate-200 bg-white/90 backdrop-blur-md px-4 sm:px-6 flex items-center justify-between sticky top-0 z-30 shadow-sm">
      <div className="flex items-center gap-3">
        {onToggleMobileMenu && (
          <button
            onClick={onToggleMobileMenu}
            className="md:hidden p-2 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors focus:outline-none"
            aria-label="Toggle Navigation Menu"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        )}
        <Link to="/dashboard" className="flex items-center gap-1.5 group">
          <img src="/android-chrome-192x192.png" alt="SensiAvera Logo" className="w-6 h-6 object-scale-down  transition-transform group-hover:scale-105" />
          <div>
            <span className="font-bold text-base sm:text-lg tracking-tight text-slate-900 flex items-center gap-1.5 sm:gap-2">
              SensiAvera

            </span>
            <p className="text-[10px] sm:text-[11px] text-slate-500 font-medium hidden xs:block">Document Intelligence Platform</p>
          </div>
        </Link>
      </div>

      <div className="flex items-center gap-2 sm:gap-4">
        {/* Subscription Status Badge */}
        <Link
          to="/subscription"
          className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-semibold transition-all ${subscription?.accessGranted
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
              : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
            }`}
        >
          <CreditCard className="w-3.5 h-3.5" />
          <span>{subscription?.accessGranted ? 'Subscription: Active' : 'Check Plan Status'}</span>
        </Link>

        <div className="flex items-center gap-2 sm:gap-3 pl-2 sm:pl-3 border-l border-slate-200">
          <div className="flex items-center gap-2">
            {user?.picture ? (
              <img src={user.picture} alt={user.name || user.email} className="w-8 h-8 rounded-full border border-slate-200 object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-cyan-100 text-cyan-800 flex items-center justify-center border border-cyan-200 font-bold text-xs shrink-0">
                {(user?.name || user?.email || 'U').charAt(0).toUpperCase()}
              </div>
            )}
            <div className="text-left hidden md:block">
              <div className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                <span>{user?.name || user?.email}</span>
                {user?.role === 'SUPER_ADMIN' && (
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold bg-gradient-to-r from-amber-500 to-indigo-600 text-white uppercase tracking-wider shadow-xs">
                    Super Admin
                  </span>
                )}
              </div>

            </div>
          </div>

          <button
            onClick={logout}
            title="Sign Out via Logto SSO"
            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
};
